import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
} from "@material-ui/core";
import ArchiveIcon from "@material-ui/icons/Archive";
import {Link} from "react-router";
import {formatDate} from "common/utils/format_date";
import {DisableColorLinkIcon} from "./data_asset_selector";
import {styled} from "styled-components";

const SelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;

  // TODO should probably use a theme for the breakpoints
  @media (max-width: 1920px) {
    flex-direction: column;
    align-items: start;
  }
`;

// Ensures the link never appears on a line on its own
const LinkWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5em;
  align-items: center;

  @media (max-width: 1920px) {
  }
`;

const styles = {
  container: {
    display: "flex",
    flex: 1,
    gap: "1em",
    alignItems: "center",
  },
  selector: {
    flex: 1,
  },
  menuItemContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  date: {
    fontSize: "0.8rem",
    color: "#757575",
    marginLeft: "8px",
  },
  archivedIcon: {
    fontSize: "1.2rem",
    color: "#9e9e9e",
  },
  smallArchivedIcon: {
    fontSize: "14px",
    color: "#9e9e9e",
  },
  dateWithIcon: {
    display: "flex",
    alignItems: "center",
  },
  outputKeyField: {
    width: "12em",
    flexShrink: 0,
  },
  versionSelector: {
    width: "200px",
    flexShrink: 0,
  },
  withError: {
    border: "1px solid red",
    borderRadius: "4px",
  },
};

const GroupedSelector = ({
  label,
  groups,
  selectedGroupId,
  selectedItemId,
  onGroupChange,
  onItemChange,
  disabled,
  showVersionSelector = true,
  linkTo,
  error,
  LATEST_ITEM = " ",
}) => {
  const isConfigurationSelector = label === "Configuration";

  const areAllConfigsArchived = group => {
    if (!isConfigurationSelector) {
      return false;
    }
    return group.items.every(item => item.is_archived);
  };

  const getItemDisplayContent = item => {
    if (isConfigurationSelector) {
      return (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          {item.is_archived && <ArchiveIcon style={styles.smallArchivedIcon} />}
          <span>
            {item.creation_date ? formatDate(item.creation_date) : item.name}
          </span>
        </Box>
      );
    }

    return (
      <div style={styles.menuItemContent}>
        <span>{item.name}</span>
        <Box display="flex" alignItems="center">
          {item.is_archived && <ArchiveIcon style={styles.smallArchivedIcon} />}
          {item.creation_date && (
            <span style={styles.date}>{formatDate(item.creation_date)}</span>
          )}
        </Box>
      </div>
    );
  };

  const getSelectedDefinitionDisplay = selected => {
    if (!selected) {
      return <em>Select {label}</em>;
    }

    const selectedGroup = groups.find(g => g.groupId === selected);
    if (!selectedGroup) {
      return null;
    }

    return (
      <div
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
        }}
        title={selectedGroup.latest.name}
      >
        {selectedGroup.latest.name}
      </div>
    );
  };

  const getSelectedItemDisplay = selected => {
    if (selected === LATEST_ITEM || !selected) {
      return <em>Use latest</em>;
    }

    const selectedGroup = groups.find(g => g.groupId === selectedGroupId);
    const item = selectedGroup?.items.find(i => i.id.toString() === selected);

    if (!item) {
      return <em>Use latest</em>;
    }

    if (isConfigurationSelector) {
      return (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          {item.is_archived && <ArchiveIcon style={styles.smallArchivedIcon} />}
          <span>
            {item.creation_date ? formatDate(item.creation_date) : item.name}
          </span>
        </Box>
      );
    }

    return (
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        {item.is_archived && <ArchiveIcon style={styles.smallArchivedIcon} />}
        <span>{item.name}</span>
      </Box>
    );
  };

  return (
    <SelectorContainer>
      <FormControl
        variant="outlined"
        size="small"
        style={styles.selector}
        disabled={disabled}
        fullWidth
      >
        <InputLabel>{label}</InputLabel>
        <Select
          value={selectedGroupId}
          onChange={e => onGroupChange(e.target.value)}
          label={label}
          renderValue={getSelectedDefinitionDisplay}
          error={error}
        >
          <MenuItem value="">
            <em>Select {label}</em>
          </MenuItem>
          {groups.map(group => (
            <MenuItem key={group.groupId} value={group.groupId}>
              <div style={styles.menuItemContent}>
                <span>{group.latest.name}</span>
                {group.latest.creation_date && (
                  <div style={styles.dateWithIcon}>
                    {areAllConfigsArchived(group) && (
                      <ArchiveIcon style={styles.archivedIcon} />
                    )}
                    <span style={styles.date}>
                      {formatDate(group.latest.creation_date)}
                    </span>
                  </div>
                )}
              </div>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {((showVersionSelector && Boolean(selectedGroupId)) ||
        (Boolean(selectedGroupId) && linkTo)) && (
        <LinkWrapper>
          {showVersionSelector && Boolean(selectedGroupId) && (
            <FormControl
              variant="outlined"
              size="small"
              style={styles.versionSelector}
              disabled={disabled}
            >
              <InputLabel>Version</InputLabel>
              <Select
                value={selectedItemId || LATEST_ITEM}
                onChange={e => onItemChange(e.target.value)}
                label="Version"
                renderValue={getSelectedItemDisplay}
              >
                <MenuItem value={LATEST_ITEM}>
                  <em>Use latest</em>
                </MenuItem>
                {groups
                  .find(g => g.groupId === selectedGroupId)
                  ?.items.map(item => (
                    <MenuItem key={item.id} value={item.id.toString()}>
                      {getItemDisplayContent(item)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {Boolean(selectedGroupId) && linkTo && (
            <Link to={linkTo}>
              <IconButton size="small" style={{padding: 0}}>
                <DisableColorLinkIcon $disabled={disabled} />
              </IconButton>
            </Link>
          )}
        </LinkWrapper>
      )}
    </SelectorContainer>
  );
};

export default GroupedSelector;
